/**
 * Rastreamento por checkout (settings.tracking): Meta Pixel, GA4 e Google
 * Ads, configurados no builder — independentes do pixel da PLATAFORMA (que
 * segue vindo de /integrations/fbpixel).
 *
 * Nada é injetado em modo preview (?_preview=1): edição no builder não pode
 * disparar evento de anúncio.
 *
 * Três regras que este módulo garante (02/10/2026):
 *
 * 1. Cada evento vai SÓ para o pixel deste checkout. `fbq('track')` envia para
 *    todos os pixels inicializados na página, então com um pixel de plataforma
 *    presente cada um recebia os eventos do outro — Purchase em dobro nos dois.
 *    Aqui é sempre `trackSingle` (Meta) e `send_to` (Google).
 * 2. Uma compra conta UMA vez por pixel. O Purchase saía no submit e de novo na
 *    página de obrigado, e a cada reload dela. A marca fica no sessionStorage
 *    por pixel + referência da compra.
 * 3. Purchase só quando houve pagamento. Pix e boleto disparavam na GERAÇÃO da
 *    cobrança; agora marcam a etapa (AddPaymentInfo) e o Purchase sai quando o
 *    pagamento confirma, onde o front fica sabendo (Pix de assinatura).
 */
import { useEffect } from 'react';
import ReactPixel from 'react-facebook-pixel';
import { ICheckoutPageSettings } from '../../../../api/checkouts/types/checkout-page.type';
import { isPreviewMode } from './use-preview-bridge';
import {
	__resetPixelEventsForTests,
	ensurePixelInitialized,
	trackInitiateCheckoutOnPixel
} from '../../../../hooks/FbPixel/pixelEvents';

type Tracking = NonNullable<ICheckoutPageSettings['tracking']>;
type Network = 'meta' | 'ga4' | 'ads';

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: unknown[]) => void;
	}
}

/** Config ativa — lida na hora do Purchase (Form/useSubmit). */
let activeTracking: Tracking | null = null;

const configuredGoogleIds = new Set<string>();

const FIRED_PREFIX = 'waid-purchase:';
const PENDING_PREFIX = 'waid-pending-purchase:';

const loadGtag = (firstId: string) => {
	if (window.gtag) return;
	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${firstId}`;
	document.head.appendChild(script);
	window.dataLayer = window.dataLayer || [];
	window.gtag = function gtag() {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer?.push(arguments);
	};
	window.gtag('js', new Date());
};

/** Inicializa cada SDK uma vez por ID, por mais que a tela remonte. */
const ensureLoaded = (tracking: Tracking) => {
	const { metaPixelId, ga4Id, googleAdsId } = tracking;

	if (metaPixelId) ensurePixelInitialized(metaPixelId);

	const googleId = ga4Id || googleAdsId;
	if (googleId) loadGtag(googleId);
	for (const id of [ga4Id, googleAdsId]) {
		if (id && !configuredGoogleIds.has(id)) {
			window.gtag?.('config', id);
			configuredGoogleIds.add(id);
		}
	}
};

/** Evento do Meta só para o pixel deste checkout. */
const metaEvent = (
	pixelId: string,
	event: string,
	data?: Record<string, unknown>,
	eventID?: string
) => {
	ReactPixel.fbq(
		'trackSingle',
		pixelId,
		event,
		data ?? {},
		...(eventID ? [{ eventID }] : [])
	);
};

const firedKey = (network: Network, id: string, reference: string) =>
	`${FIRED_PREFIX}${network}:${id}:${reference}`;

const wasFired = (key: string): boolean => {
	try {
		return sessionStorage.getItem(key) === '1';
	} catch {
		// Sem storage não há como deduplicar: melhor contar que perder.
		return false;
	}
};

const markFired = (key: string) => {
	try {
		sessionStorage.setItem(key, '1');
	} catch {
		// segue sem persistir
	}
};

/**
 * Purchase/conversion, uma vez por pixel e por compra. Sem `reference` não há
 * como deduplicar — quem chama decide se dispara assim mesmo.
 */
const firePurchase = (
	tracking: Tracking,
	{ value, reference }: { value?: number; reference?: string }
) => {
	const { metaPixelId, ga4Id, googleAdsId, googleAdsLabel } = tracking;
	const amount = value ? { currency: 'BRL', value } : {};

	const once = (network: Network, id: string, send: () => void) => {
		const key = reference ? firedKey(network, id, reference) : null;
		if (key && wasFired(key)) return;
		send();
		if (key) markFired(key);
	};

	if (metaPixelId) {
		once('meta', metaPixelId, () =>
			metaEvent(
				metaPixelId,
				'Purchase',
				{ currency: 'BRL', ...amount },
				reference
			)
		);
	}
	if (ga4Id) {
		once('ga4', ga4Id, () =>
			window.gtag?.('event', 'purchase', {
				send_to: ga4Id,
				...amount,
				...(reference ? { transaction_id: reference } : undefined)
			})
		);
	}
	if (googleAdsId) {
		once('ads', googleAdsId, () =>
			window.gtag?.('event', 'conversion', {
				send_to: googleAdsLabel
					? `${googleAdsId}/${googleAdsLabel}`
					: googleAdsId,
				...amount,
				...(reference ? { transaction_id: reference } : undefined)
			})
		);
	}
};

/**
 * `kind` separa as etapas do funil: 'checkout' marca visita + intenção
 * (InitiateCheckout/begin_checkout; o Purchase sai no submit); 'confirmation'
 * é a página de obrigado — PageView, e o Purchase só se ainda não foi contado
 * para aquele pixel naquela compra (pixel diferente do checkout, por exemplo).
 *
 * `reference` é o comprovante da compra. Sem ele a página de obrigado NÃO
 * dispara Purchase: abrir `/p/thanks/...?v=9999` na mão não é uma venda.
 */
export const useCheckoutTracking = (
	tracking?: Tracking,
	kind: 'checkout' | 'confirmation' | 'landing' = 'checkout',
	/** Valor da compra — enriquece o Purchase da página de obrigado. */
	value?: number,
	reference?: string | null
) => {
	useEffect(() => {
		if (!tracking || isPreviewMode()) return;
		const { metaPixelId, ga4Id, googleAdsId } = tracking;
		if (!metaPixelId && !ga4Id && !googleAdsId) return;

		if (kind === 'checkout') activeTracking = tracking;

		// `config` do GA4 já envia o page_view.
		ensureLoaded(tracking);

		if (metaPixelId) {
			metaEvent(metaPixelId, 'PageView');
			// Um por pixel por página: se o pixel do builder for o mesmo da
			// plataforma, ele não recebe dois.
			if (kind === 'checkout') trackInitiateCheckoutOnPixel(metaPixelId);
		}
		if (ga4Id && kind === 'checkout') {
			window.gtag?.('event', 'begin_checkout', { send_to: ga4Id });
		}

		if (kind === 'confirmation' && reference) {
			firePurchase(tracking, { value, reference });
		}
	}, [tracking, kind, value, reference]);
};

/**
 * Chamado no submit, antes do redirect. Os SDKs enviam por beacon, então o
 * replace da página não perde o evento na prática.
 *
 * `approved` diz se o dinheiro já entrou (cartão aprovado, gratuito). Pix e
 * boleto só geraram a cobrança: marcam a etapa de pagamento e deixam o
 * Purchase pendente para `confirmPendingPurchase`.
 */
export const trackPurchase = ({
	value,
	reference,
	approved = true
}: {
	value: number;
	reference?: string;
	approved?: boolean;
}) => {
	const tracking = activeTracking;
	if (!tracking) return;

	if (approved) {
		firePurchase(tracking, { value, reference });
		return;
	}

	const { metaPixelId, ga4Id } = tracking;
	if (metaPixelId) {
		metaEvent(metaPixelId, 'AddPaymentInfo', { currency: 'BRL', value });
	}
	if (ga4Id) {
		window.gtag?.('event', 'add_payment_info', {
			send_to: ga4Id,
			currency: 'BRL',
			value
		});
	}

	if (!reference) return;
	try {
		sessionStorage.setItem(
			`${PENDING_PREFIX}${reference}`,
			JSON.stringify({ tracking, value })
		);
	} catch {
		// sem storage o Purchase da confirmação não sai — não há onde guardar
	}
};

/**
 * Pagamento pendente confirmou (tela de espera do Pix): dispara o Purchase que
 * o submit deixou guardado. Idempotente por pixel + referência.
 */
export const confirmPendingPurchase = (reference?: string | null) => {
	if (!reference || isPreviewMode()) return;

	let pending: { tracking?: Tracking; value?: number } | null = null;
	try {
		const raw = sessionStorage.getItem(`${PENDING_PREFIX}${reference}`);
		pending = raw ? JSON.parse(raw) : null;
	} catch {
		pending = null;
	}
	if (!pending?.tracking) return;

	ensureLoaded(pending.tracking);
	firePurchase(pending.tracking, { value: pending.value, reference });

	try {
		sessionStorage.removeItem(`${PENDING_PREFIX}${reference}`);
	} catch {
		// a marca de "já disparado" acima já impede a repetição
	}
};

/** Só para testes: zera o estado de módulo entre casos. */
export const __resetTrackingForTests = () => {
	activeTracking = null;
	configuredGoogleIds.clear();
	__resetPixelEventsForTests();
};
